import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { InjectionToken } from '@angular/core';
import { sameAccount } from 'app/modules/main/models/account';
import { Account } from 'app/modules/main/models/account';
import { ErrorMessage } from 'app/modules/main/models/authorization';
import { Access } from 'app/modules/main/models/authorization';
import { Authorization } from 'app/modules/main/models/authorization';
import { UserPermissions } from 'app/modules/main/models/user-permissions';
import { OAuthCodeFlowService } from 'app/modules/main/services/oauth-code-flow.service';
import { UserPermissionsService } from 'app/modules/main/services/user-permissions.service';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { map } from 'rxjs/operators';

export const AUTH_PROVIDER_SCOPE_PREFIX = new InjectionToken<number>('PROVIDER_SCOPE_PREFIX');
export const AUTH_USER_SCOPE_PREFIX = new InjectionToken<number>('USER_SCOPE_PREFIX');

@Injectable({ providedIn: 'root' })
export class UserAuthorizer {

  constructor(
    private readonly oAuthCodeFlowService: OAuthCodeFlowService,
    private readonly userPermissionsService: UserPermissionsService,
    @Inject(AUTH_PROVIDER_SCOPE_PREFIX) private readonly providerScopePrefix: string,
    @Inject(AUTH_USER_SCOPE_PREFIX) private readonly userScopePrefix: string,
  ) {
  }

  authorizeUserForAccount<A extends Account>(
    account: A
  ): Observable<Authorization<A>> {

    return this.oAuthCodeFlowService.authorize()
      .pipe(switchMap(access => this.promoteAccessToAuthorization(account, access)));
  }

  refresh<A extends Account>(
    authorization: Authorization<A>
  ): Observable<Authorization<A>> {

    return this.oAuthCodeFlowService.refresh()
      .pipe(switchMap(access => this.promoteAccessToAuthorization(authorization.account, access)));
  }

  private promoteAccessToAuthorization<A extends Account>(
    account: A,
    access: Access,
  ): Observable<Authorization<A>> {
    return this.userPermissionsService.fetch(access)
      .pipe(map(permissions => this.buildAuthorization(account, access, permissions)));
  }

  private buildAuthorization<A extends Account>(
    account: A,
    access: Access,
    permissions: UserPermissions
  ): Authorization<A> {

    const scopes = this.filterScopes(account, access, permissions);

    if (scopes.length > 0) {

      return {
        ...access,
        account,
        scopes,
      };
    }

    throw ErrorMessage.NOT_AUTHORISED;
  }

  private filterScopes<A extends Account>(
    account: A,
    access: Access,
    userPermissions: UserPermissions,
  ): string[] {

    const providerScopes = this.filterProviderScopes(access);
    const userScopesForAccount = this.filterUserScopes(account, userPermissions);

    return providerScopes.concat(userScopesForAccount);
  }

  private filterProviderScopes(
    access: Access
  ): string[] {

    return Array.from(access.scopes)
      .filter(scope => scope.startsWith(this.providerScopePrefix))
      .map(scope => scope.replace(this.providerScopePrefix, ''));
  }

  private filterUserScopes<A extends Account>(
    account: A,
    userPermissions: UserPermissions
  ): string[] {

    return Array.from(userPermissions.accounts)
      .filter(permission => sameAccount(account, permission.account))
      .flatMap(permission => Array.from(permission.scopes))
      .filter(scope => scope.startsWith(this.userScopePrefix))
      .map(scope => scope.replace(this.userScopePrefix, ''));
  }

}
